import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Link,
  TablePagination,
} from "@mui/material";
import { PiSortDescendingLight, PiSortAscendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { FaEdit, FaTrash } from "react-icons/fa";
import MultiSelect from "../../../components/MultiSelect";
import authService from "../../../utils/authService";
import { getAdminCompanyListCached } from "../../../utils/adminCompanyCache";

type ShowData = "email" | "phone" | "email_phone" | null;

interface JobProfile {
  id?: number;
  title: string;
  description: string;
  company_id: number | string;
  primary_contact_id: number | string;
  primary_contact_name?: string;
  contact_ids?: number[];
  location?: string;
  ctc?: string;
  number_of_positions?: number;
  job_category?: string;
  exp_on: string;
  status: string;
  show_data?: ShowData;
  del_row?: boolean;
  selected_count?: number;
  shortlisted_count?: number;
}

interface Company {
  company_id: string;
  company_name: string;
  del_row?: boolean;
}

interface Contact {
  id: number;
  name: string;
  del_row?: boolean;
}

const STATUS_OPTIONS = ["open", "closed", "in_progress"];

const SHOW_DATA_OPTIONS: { label: string; value: ShowData }[] = [
  { label: "Show Email", value: "email" },
  { label: "Show Phone", value: "phone" },
  { label: "Show Email & Phone", value: "email_phone" },
  { label: "Hide Email & Phone", value: null },
];

/** Select string: empty = omit from API (create / unchanged); "__null__" = API null */
const SHOW_DATA_NULL_SENTINEL = "__null__";

const showDataToSelectValue = (v: ShowData | undefined): string => {
  if (v === null) return SHOW_DATA_NULL_SENTINEL;
  if (v === "email" || v === "phone" || v === "email_phone") return v;
  return "";
};

const selectValueToShowData = (selectValue: string): ShowData | undefined => {
  if (selectValue === "") return undefined;
  if (selectValue === SHOW_DATA_NULL_SENTINEL) return null;
  if (selectValue === "email" || selectValue === "phone" || selectValue === "email_phone") {
    return selectValue;
  }
  return undefined;
};

const showDataLabel = (v: ShowData | undefined): string => {
  if (v === null) return "Hidden";
  if (v === "email") return "Email";
  if (v === "phone") return "Phone";
  if (v === "email_phone") return "Email & Phone";
  return "—";
};

const isAllowedNumericKey = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  if (e.ctrlKey || e.metaKey) return true;
  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
  return allowedKeys.includes(e.key) || /^\d$/.test(e.key);
};

const emptyForm = (): JobProfile => ({
  title: "",
  description: "",
  company_id: "",
  primary_contact_id: "",
  location: "",
  ctc: "",
  number_of_positions: undefined,
  job_category: "",
  exp_on: "",
  status: "open",
});

const JobProfilePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [jobProfiles, setJobProfiles] = useState<JobProfile[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("new");
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(true);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [formData, setFormData] = useState<JobProfile>(emptyForm());
  const [error, setError] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedProfile, setSelectedProfile] = useState<JobProfile>();
  const [formContactIds, setFormContactIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  /** Bootstrap select value; empty = omit from API on create / unchanged on edit when no field */
  const [showDataSelect, setShowDataSelect] = useState("");
  /** Snapshot when edit modal opened — omit `show_data` on update if unchanged */
  const initialShowDataRef = useRef<ShowData | undefined>(undefined);
  /** For rendering optional empty row in edit when profile had no show_data */
  const [modalInitialShowData, setModalInitialShowData] = useState<ShowData | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoadingCompanies(true);
      try {
        const { companies } = await getAdminCompanyListCached();
        if (mounted) {
          setCompanies(
            ((companies || []) as Company[]).filter((c) => !c.del_row)
          );
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        if (mounted) setLoadingCompanies(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const companyFromUrl = searchParams.get("company");
    const statusFromUrl = searchParams.get("status");
    if (companyFromUrl && companies.length > 0) {
      const exists = companies.some((c) => String(c.company_id) === companyFromUrl);
      if (exists) setSelectedCompany(companyFromUrl);
    }
    if (statusFromUrl && ["open", "closed", "in_progress"].includes(statusFromUrl)) {
      setFilterStatus(statusFromUrl);
    }
  }, [searchParams, companies]);

  useEffect(() => {
    if (selectedCompany) {
      fetchJobProfiles(selectedCompany, filterStatus);
      fetchContactsByCompany(selectedCompany);
    } else {
      setJobProfiles([]);
      setContacts([]);
    }
  }, [selectedCompany, filterStatus]);

  useEffect(() => {
    setPage(0);
  }, [selectedCompany, filterStatus, search, sortField, sortDirection]);

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const { companies } = await getAdminCompanyListCached();
      setCompanies(
        ((companies || []) as Company[]).filter((c) => !c.del_row)
      );
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchJobProfiles = async (companyId: string, status?: string) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      const query = params.toString();
      const response = await authService.get(
        `placement-officer/job-profiles/company/${companyId}${query ? `?${query}` : ""}`
      );
      setJobProfiles(response.data.job_profiles || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching job profiles:", error);
    }
  };

  const fetchContactsByCompany = async (companyId: string | number) => {
    try {
      const allContacts: Contact[] = [];
      let nextPage = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await authService.get(
          `placement-officer/company-contacts/company/${companyId}?page=${nextPage}&count=100`
        );
        const payload = response.data ?? {};
        const rows = Array.isArray(payload.contacts) ? payload.contacts : [];
        allContacts.push(...rows);

        if (typeof payload.has_next === "boolean") {
          hasMore = payload.has_next;
        } else if (typeof payload.total === "number") {
          hasMore = allContacts.length < payload.total;
        } else {
          hasMore = rows.length === 100;
        }
        nextPage += 1;
      }

      setContacts(allContacts.filter((c: Contact) => !c.del_row));
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts([]);
    }
  };

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedCompany(value);
    setSearch("");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("company", value);
        else next.delete("company");
        return next;
      },
      { replace: true }
    );
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFilterStatus(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("status", value);
        else next.delete("status");
        return next;
      },
      { replace: true }
    );
  };

  const handleModal = (type: string, profile?: JobProfile): void => {
    setError("");
    setModalType(type);
    if (type === "new") {
      const newForm = { ...emptyForm(), company_id: selectedCompany };
      setFormData(newForm);
      setFormContactIds([]);
      setShowDataSelect("");
      initialShowDataRef.current = undefined;
      setModalInitialShowData(undefined);
      fetchContactsByCompany(selectedCompany);
    } else if (profile) {
      setFormData(profile);
      const existing = profile.contact_ids ?? [];
      const primaryId = Number(profile.primary_contact_id);
      const merged = Array.from(new Set([primaryId, ...existing].filter(Boolean)));
      setFormContactIds(merged.map(String));
      const initialSd: ShowData | undefined =
        profile.show_data === null ? null : profile.show_data ?? undefined;
      initialShowDataRef.current = initialSd;
      setModalInitialShowData(initialSd);
      setShowDataSelect(showDataToSelectValue(initialSd));
      fetchContactsByCompany(profile.company_id);
    }
    setShowModal(true);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ): void => {
    const { name, value } = e.target;
    if (name === "title") {
      setFormData((prev) => ({ ...prev, [name]: value.replace(/[^a-zA-Z\s\-]/g, "") }));
      return;
    }
    if (name === "number_of_positions") {
      const v = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, [name]: v === "" ? undefined : parseInt(v, 10) }));
      return;
    }
    if (name === "ctc") {
      const v = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, [name]: v }));
      return;
    }
    if (name === "primary_contact_id") {
      const newPrimary = value;
      setFormData((prev) => ({ ...prev, primary_contact_id: newPrimary }));
      if (newPrimary) {
        setFormContactIds((prev) => Array.from(new Set([newPrimary, ...prev.filter((id) => id !== newPrimary)])));
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>, field: string) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async (): Promise<void> => {
    setError("");

    if (!formData.title?.trim()) {
      setError("Title is required");
      return;
    }
    if (!/^[a-zA-Z0-9\s\-]+$/.test(formData.title.trim())) {
      setError("Title must contain only letters, numbers, spaces and hyphens");
      return;
    }
    if (!formData.description?.trim()) {
      setError("Description is required");
      return;
    }
    if (!formData.primary_contact_id || String(formData.primary_contact_id).trim() === "") {
      setError("Primary contact is required");
      return;
    }
    if (!formData.exp_on) {
      setError("Expiry date is required");
      return;
    }
    if (!formData.status) {
      setError("Status is required");
      return;
    }

    try {
      setLoading(true);
      let jpId: number;
      if (modalType === "new") {
        const createBody: Record<string, unknown> = {
          title: formData.title,
          description: formData.description,
          company_id: Number(selectedCompany) || selectedCompany,
          primary_contact_id: Number(formData.primary_contact_id) || formData.primary_contact_id,
          location: formData.location || null,
          ctc: formData.ctc || null,
          number_of_positions: formData.number_of_positions ?? null,
          job_category: formData.job_category || null,
          exp_on: formData.exp_on,
          status: formData.status,
          created_by: sessionStorage.getItem("Email") || "",
        };
        if (showDataSelect !== "") {
          createBody.show_data = selectValueToShowData(showDataSelect);
        }
        const res = await authService.post("placement-officer/job-profiles/create", createBody);
        jpId = res.data?.jp_id;
      } else {
        const updateBody: Record<string, unknown> = {
          id: formData.id,
          title: formData.title,
          description: formData.description,
          primary_contact_id: Number(formData.primary_contact_id) || formData.primary_contact_id,
          location: formData.location || null,
          ctc: formData.ctc || null,
          number_of_positions: formData.number_of_positions ?? null,
          job_category: formData.job_category || null,
          exp_on: formData.exp_on,
          status: formData.status,
          updated_by: sessionStorage.getItem("Email") || "",
        };
        const currentSd = selectValueToShowData(showDataSelect);
        const initialSd = initialShowDataRef.current;
        if (currentSd !== initialSd && currentSd !== undefined) {
          updateBody.show_data = currentSd;
        }
        await authService.put("placement-officer/job-profiles/update", updateBody);
        jpId = formData.id!;
      }
      const idsToSync = formContactIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
      if (idsToSync.length > 0) {
        await authService.put("placement-officer/job-profiles/contacts/sync", {
          jp_id: jpId,
          company_contact_ids: idsToSync,
        });
      }
      setLoading(false);
      setShowModal(false);
      fetchJobProfiles(selectedCompany, filterStatus);
    } catch (err: any) {
      setLoading(false);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setError(msg);
    }
  };

  const isFormValid =
    !!formData.title?.trim() &&
    !!formData.description?.trim() &&
    !!formData.primary_contact_id &&
    String(formData.primary_contact_id).trim() !== "" &&
    !!formData.exp_on &&
    !!formData.status;

  const handleDelete = (profile: JobProfile): void => {
    setSelectedProfile(profile);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (selectedProfile) {
      try {
        setDeleting(true);
        await authService.delete("placement-officer/job-profiles/delete", {
          id: selectedProfile.id,
        });
        setDeleting(false);
        fetchJobProfiles(selectedCompany, filterStatus);
        setShowDeleteModal(false);
      } catch (error) {
        setDeleting(false);
        console.error("Error deleting job profile:", error);
      }
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortedProfiles = (): JobProfile[] => {
    const filtered = jobProfiles
      .filter((p) => !p.del_row)
      .filter(
        (p) => {
          const q = (search ?? "").toString().toLowerCase();
          return (
            (p.title ?? "").toString().toLowerCase().includes(q) ||
            (p.description ?? "").toString().toLowerCase().includes(q) ||
            (p.status ?? "").toString().toLowerCase().includes(q)
          );
        }
      );

    if (!sortField) return filtered;

    const numericFields = ["selected_count", "shortlisted_count"];
    const isNumericSort = numericFields.includes(sortField);

    return [...filtered].sort((a, b) => {
      const rawA = a[sortField as keyof JobProfile];
      const rawB = b[sortField as keyof JobProfile];

      if (isNumericSort) {
        const numA = typeof rawA === "number" ? rawA : Number(rawA) || 0;
        const numB = typeof rawB === "number" ? rawB : Number(rawB) || 0;
        return sortDirection === "asc" ? numA - numB : numB - numA;
      }

      if (sortField === "contact_ids") {
        const lenA = Array.isArray(rawA) ? rawA.length : 0;
        const lenB = Array.isArray(rawB) ? rawB.length : 0;
        return sortDirection === "asc" ? lenA - lenB : lenB - lenA;
      }

      const aStr =
        rawA != null && typeof rawA === "object" && !Array.isArray(rawA)
          ? ""
          : (rawA != null ? String(rawA) : "");
      const bStr =
        rawB != null && typeof rawB === "object" && !Array.isArray(rawB)
          ? ""
          : (rawB != null ? String(rawB) : "");
      const aLower = typeof aStr === "string" ? aStr.toLowerCase() : "";
      const bLower = typeof bStr === "string" ? bStr.toLowerCase() : "";
      return sortDirection === "asc"
        ? aLower.localeCompare(bLower)
        : bLower.localeCompare(aLower);
    });
  };

  const sortedProfiles = getSortedProfiles();
  const paginatedProfiles = sortedProfiles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const SortIcon = ({ field }: { field: string }) =>
    sortField === field ? (
      sortDirection === "asc" ? (
        <PiSortDescendingLight size={16} />
      ) : (
        <PiSortAscendingLight size={16} />
      )
    ) : (
      <HiOutlineDotsVertical size={16} />
    );

  const columns: { label: string; field: string }[] = [
    { label: "Title", field: "title" },
    { label: "Description", field: "description" },
    { label: "Primary Contact", field: "primary_contact_name" },
    { label: "Contacts", field: "contact_ids" },
    { label: "Contact visibility", field: "show_data" },
    { label: "Selected", field: "selected_count" },
    { label: "Shortlisted", field: "shortlisted_count" },
    { label: "Expiry Date", field: "exp_on" },
    { label: "Status", field: "status" },
  ];

  const handleTitleClick = (profile: JobProfile) => {
    if (profile.id && selectedCompany) {
      navigate(`/placement-officer/students?company=${selectedCompany}&job_profile=${profile.id}`);
    }
  };

  const resolveContactName = (profile: JobProfile): string => {
    if (profile.primary_contact_name) return profile.primary_contact_name;
    const match = contacts.find((c) => String(c.id) === String(profile.primary_contact_id));
    return match ? match.name : String(profile.primary_contact_id ?? "-");
  };

  const statusBadgeClass = (status: string) => {
    if (status === "open") return "badge bg-success";
    if (status === "closed") return "badge bg-danger";
    if (status === "in_progress") return "badge bg-warning text-dark";
    return "badge bg-secondary";
  };

  return (
    <Box
      className="border rounded-2 bg-white my-2 me-2"
      sx={{
        height: "calc(100vh - 20px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top bar */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        flexShrink={0}
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="jp-company-label">Company</InputLabel>
            <Select
              labelId="jp-company-label"
              value={selectedCompany}
              onChange={handleCompanyChange}
              label="Company"
              sx={{ height: 36 }}
            >
              <MenuItem value="">Select Company</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.company_id} value={c.company_id}>
                  {c.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }} disabled={!selectedCompany}>
            <InputLabel id="jp-status-label">Status</InputLabel>
            <Select
              labelId="jp-status-label"
              value={filterStatus}
              onChange={handleStatusChange}
              label="Status"
              sx={{ height: 36 }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace("_", " ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={!selectedCompany}
            sx={{ minWidth: 200 }}
          />
        </Box>

        {selectedCompany && (
          <button
            className="btn"
            onClick={() => handleModal("new")}
          >
            + New Job Profile
          </button>
        )}
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        className="px-2"
        sx={{ flexGrow: 1, overflow: "auto", borderRadius: 0 }}
      >
        <Table className="border border-2" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className="muifont mui-header-cell" sx={{ color: "text.secondary" }}>
                S.No
              </TableCell>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  className="muifont mui-header-cell mui-sortable-header"
                  onClick={() => handleSort(col.field)}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {col.label}
                    </Box>
                    <Box component="span" sx={{ ml: 0.5 }}>
                      <SortIcon field={col.field} />
                    </Box>
                  </Box>
                </TableCell>
              ))}
              <TableCell className="muifont mui-header-cell" sx={{ borderRight: "none" }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(searchParams.get("company") && (loadingCompanies || !selectedCompany || loading)) ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 2.5, px: 2.5 }}>
                  <Spinner animation="border" size="sm" />
                </TableCell>
              </TableRow>
            ) : !selectedCompany ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 2.5, px: 2.5, color: "text.secondary" }}>
                  Please select a company to view job profiles
                </TableCell>
              </TableRow>
            ) : loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 2.5, px: 2.5 }}>
                  <Spinner animation="border" size="sm" />
                </TableCell>
              </TableRow>
            ) : sortedProfiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 2.5, px: 2.5, color: "text.secondary" }}>
                  No job profiles found
                </TableCell>
              </TableRow>
            ) : (
              paginatedProfiles.map((profile, index) => (
                <TableRow
                  key={profile.id ?? index}
                  sx={{
                    bgcolor: index % 2 === 0 ? "background.paper" : "grey.50",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                >
                  <TableCell className="muifont mui-body-cell">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell className="muifont mui-body-cell">
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() =>
                        navigate(`/placement-officer/students?company=${profile.company_id ?? ""}&job_profile=${profile.id ?? ""}`)
                      }
                      sx={{ cursor: "pointer", textDecoration: "underline", verticalAlign: "baseline" }}
                    >
                      {profile.title}
                    </Link>
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    <span title={profile.description}>
                      {profile.description?.length > 50
                        ? profile.description.slice(0, 50) + "…"
                        : profile.description}
                    </span>
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">{resolveContactName(profile)}</TableCell>
                  <TableCell className="muifont mui-body-cell">{profile.contact_ids?.length ?? 0}</TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {showDataLabel(profile.show_data)}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">{profile.selected_count ?? 0}</TableCell>
                  <TableCell className="muifont mui-body-cell">{profile.shortlisted_count ?? 0}</TableCell>
                  <TableCell className="muifont mui-body-cell">{profile.exp_on}</TableCell>
                  <TableCell className="muifont mui-body-cell">
                    <span className={statusBadgeClass(profile.status ?? "")}>
                      {(profile.status ?? "").replace("_", " ")}
                    </span>
                  </TableCell>
                  <TableCell className="muifont mui-body-cell" sx={{ borderRight: "none" }}>
                    <Box
                      component="span"
                      className="fs-6 me-2"
                      role="button"
                      title="Edit"
                      onClick={() => handleModal("edit", profile)}
                      sx={{ cursor: "pointer", display: "inline-flex" }}
                    >
                      <FaEdit />
                    </Box>
                    <Box
                      component="span"
                      className="fs-6 me-2"
                      title="Delete"
                      role="button"
                      onClick={() => handleDelete(profile)}
                      sx={{ cursor: "pointer", display: "inline-flex" }}
                    >
                      <FaTrash />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedCompany && sortedProfiles.length > 0 && (
        <TablePagination
          component="div"
          count={sortedProfiles.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            flexShrink: 0,
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#fafafa",
            "& .MuiTablePagination-toolbar": { minHeight: 44, px: 2 },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "12px",
              color: "#666",
              mb: 0,
            },
            "& .MuiTablePagination-actions": { ml: 1 },
            "& .MuiIconButton-root": { p: "6px" },
            "& .Mui-disabled": { opacity: 0.45 },
          }}
        />
      )}

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === "new" ? "Add New Job Profile" : "Edit Job Profile"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => e.preventDefault()}>
            {error && <p className="text-danger mb-2">{error}</p>}

            <Form.Group className="mt-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title ?? ""}
                onChange={handleChange}
                placeholder="Enter Title"
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description ?? ""}
                onChange={handleChange}
                placeholder="Enter Description"
              />
            </Form.Group>

            <div className="row mt-3 g-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Primary Contact</Form.Label>
                  <Form.Select
                    name="primary_contact_id"
                    value={String(formData.primary_contact_id)}
                    onChange={handleChange}
                  >
                    <option value="">Select Contact</option>
                    {contacts.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Contacts</Form.Label>
                  <MultiSelect
                    options={contacts.map((c) => ({
                      value: String(c.id),
                      label: c.name,
                      disabled: String(c.id) === String(formData.primary_contact_id),
                    }))}
                    value={formContactIds}
                    onChange={(vals: string[]) => setFormContactIds(vals)}
                    placeholder="Select contacts"
                    disabled={contacts.length === 0}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location ?? ""}
                    onChange={handleChange}
                    placeholder="e.g. Bangalore, Karnataka"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>CTC</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    name="ctc"
                    value={formData.ctc ?? ""}
                    onChange={handleChange}
                    placeholder="e.g. 1015"
                    onKeyDown={(e) => {
                      if (!isAllowedNumericKey(e)) e.preventDefault();
                    }}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Number of Positions</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    name="number_of_positions"
                    value={formData.number_of_positions ?? ""}
                    onChange={handleChange}
                    placeholder="e.g. 3"
                    onKeyDown={(e) => {
                      if (!isAllowedNumericKey(e)) e.preventDefault();
                    }}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Job Category</Form.Label>
                  <Form.Control
                    type="text"
                    name="job_category"
                    value={formData.job_category ?? ""}
                    onChange={handleChange}
                    placeholder="e.g. Full-time"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Expiry Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="exp_on"
                    value={formData.exp_on ?? ""}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status ?? "open"}
                    onChange={handleChange}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s.replace("_", " ")}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-12">
                <Form.Group>
                  <Form.Label>Contact visibility (email and phone)</Form.Label>
                  <Form.Select
                    value={showDataSelect}
                    onChange={(e) => setShowDataSelect(e.target.value)}
                  >
                    {(modalType === "new" || modalInitialShowData === undefined) && (
                      <option value="">
                        {modalType === "new"
                          ? "— Optional: server default —"
                          : "— Not set —"}
                      </option>
                    )}
                    {SHOW_DATA_OPTIONS.map((o) => (
                      <option
                        key={o.label}
                        value={o.value === null ? SHOW_DATA_NULL_SENTINEL : o.value}
                      >
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading || !isFormValid}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the job profile:{" "}
            <strong>{selectedProfile?.title}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            No
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Deleting...
              </>
            ) : (
              "Yes, Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

    </Box>
  );
};

export default JobProfilePage;
